import {
  forwardRef, memo, useEffect, useState, useMemo, useCallback,
} from 'react';
import type { ReactNode } from 'react';
import Form from 'react-bootstrap/Form';
import ReactSelect from 'react-select';
import ReactSelectCreatable from 'react-select/creatable';
import { Button } from 'modules/core/components/buttons';

import { prepareSelectOptions } from './utils';
import { ColorSwatch } from './colorSwatch';
import type { SelectOption, DisabledOption, SelectChangeEvent } from './select.types';

// Стабильная ссылка для дефолта data (Rule 3 skill remove-defaultprops)
const emptyData: unknown[] = [];

// Стабильные ссылки для дефолтных пропсов react-select: иначе на каждом рендере
// создавались бы новые функции/объекты и заставляли react-select ре-рендериться.
const defaultNoOptionsMessage = () => 'Нет вариантов';
const menuPortalStyles = { menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) };

// react-select сам по себе не обёрнут в React.memo: даже при ссылочно-стабильных пропсах
// родителя (см. commonProps ниже) он перерисовывался на каждый рендер SimpleSelectInput.
// memo-обёртка позволяет реально пропустить рендер, когда все пропсы стабильны.
// ВАЖНО: обёртки обязаны жить на уровне модуля (не внутри компонента) — иначе каждый
// рендер порождал бы новый тип и React размонтировал бы селект (потеря фокуса/меню/значения).
// ref проходит сквозь memo транзитом: ReactSelect/Creatable — forwardRef-компоненты,
// и внутреннее состояние react-select (открытое меню, введённый текст) мемоизацией не блокируется.
const MemoizedReactSelect = memo(ReactSelect);
const MemoizedReactSelectCreatable = memo(ReactSelectCreatable);

/**
 * Пропсы «глупого» одиночного селекта (без Formik).
 *
 * Наследует нативные пропсы react-select (placeholder, menuPlacement, noOptionsMessage,
 * isClearable, isOptionDisabled, isLoading, ...) через `...rest`. Через `Omit` убраны только
 * ключи с конфликтующей сигнатурой — `value` и `onChange`: у нас своя семантика
 * (`value: string|number|null`, `onChange(e: SelectChangeEvent)`), отличная от react-select.
 * Остальные нативные пропсы протекают к потребителю как есть.
 *
 * Formik-обёртка — SelectInput (MR-3).
 */
export interface SimpleSelectInputProps
  extends Omit<React.ComponentProps<typeof ReactSelect>, 'value' | 'onChange'> {
  // для полей ввода не привязанных к форме имя не обязательно
  name?: string;
  controlId?: string;
  label?: ReactNode;
  error?: string;
  required?: boolean;
  disabled?: boolean; // наш алиас (→ isDisabled внутри)
  loading?: boolean; // наш алиас (→ isLoading внутри)
  isNextField?: boolean;
  value?: string | number | null;
  onChange?: (e: SelectChangeEvent) => void;
  /** Массив с данными для options (prepareSelectOptions превратит в SelectOption[]) */
  data?: unknown[];
  /**
   * Какое поле элемента массива данных использовать в качестве заголовка опции.
   * Строка (имя поля) или функция(item).
   */
  dataTitle?: string | ((item: any) => unknown);
  /**
   * Какое поле элемента массива данных использовать в качестве значения опции.
   * Строка (имя поля) или функция(item).
   */
  dataValue?: string | ((item: any) => unknown);
  /**
   * Сортировать ли options по значению.
   * @see https://developer.mozilla.org/ru/docs/Web/JavaScript/Reference/Global_Objects/Array/sort
   */
  sort?: boolean | ((...args: any[]) => unknown);
  /**
   * clearable - можно ли занулить значение.
   * isSearchable - можно ли вводить руками значение (поиск).
   * @see https://react-select.com/props#api
   */
  clearable?: boolean;
  isSearchable?: boolean;
  isCreatable?: boolean;
  onCreateOption?: (value: string) => void;
  rowDesign?: boolean;
  rowDesignWithoutLabel?: boolean;
  /**
   * Класс для корневого Form.Group (например, 'mb-0' при размещении инпута в ячейке
   * таблицы). Аналог rootClassName у SimpleStringInput.
   */
  rootClassName?: string;
  /**
   * Включает отображение цветовых индикаторов (квадрат/круг) рядом с опциями.
   * Если не указан (undefined), то индикация включается автоматически,
   * когда хотя бы один объект в data имеет поле color с непустым значением.
   * Явное false — полностью отключает индикаторы (старое поведение).
   */
  showColorIndicator?: boolean;
  /**
   * Отображать ли цветовой индикатор в поле выбранного значения (SingleValue).
   * Работает только если showColorIndicator=true или автоопределение включено.
   * По умолчанию false — индикатор виден только в выпадающем списке.
   */
  colorInSelectedValue?: boolean;
  /**
   * Форма цветового индикатора. Допустимые значения: 'square' (квадрат) или 'circle' (круг).
   * По умолчанию 'circle'.
   */
  indicatorShape?: 'square' | 'circle';
  /**
   * Массив опций, которые нужно сделать недоступными для выбора.
   * id — значение опции (соответствует dataValue).
   * error — текст ошибки, который будет показан под названием опции красным цветом (класс text-danger).
   * Сама опция визуально становится светлее (opacity, серый текст) и не выбирается.
   */
  disabledOptions?: DisabledOption[];
}

const SimpleSelectInput = forwardRef<HTMLElement, SimpleSelectInputProps>((props, ref) => {
  const [currentValue, setCurrentValue] = useState<SelectOption | null>(null);
  const [options, setOptions] = useState<SelectOption[]>([]);

  const {
    controlId,
    name,
    label,
    data = emptyData,
    dataTitle,
    dataValue,
    value,
    error,
    loading,
    clearable = true,
    disabled,
    required,
    sort,
    onChange,
    isSearchable = true,
    isCreatable = false,
    onCreateOption,
    rowDesign = false,
    rowDesignWithoutLabel = false,
    rootClassName,
    isNextField,
    showColorIndicator: forceShowColorIndicator = undefined,
    colorInSelectedValue = false,
    indicatorShape = 'circle',
    disabledOptions,
    // Нативные пропсы react-select достаиваем из rest явно по двум причинам:
    //  1) дать consumer-у приоритет над нашими алиасами (clearable/loading/disabled);
    //  2) не позволить `...rest` (который теперь идёт первым в commonProps) перебить
    //     нашу внутреннюю логику — isOptionDisabled/disabledOptions, formatOptionLabel
    //     (цветовые индикаторы/disabled-reason), styles.menuPortal.zIndex.
    isClearable: restIsClearable,
    isLoading: restIsLoading,
    isDisabled: restIsDisabled,
    isOptionDisabled: restIsOptionDisabled,
    className: restClassName,
    classNamePrefix: restClassNamePrefix,
    noOptionsMessage: restNoOptionsMessage,
    ...rest
  } = props;

  // Подготовка карты disabled опций
  const disabledOptionsMap = useMemo(() => {
    if (!Array.isArray(disabledOptions)) return new Map();
    return new Map(disabledOptions.map(item => [item.id, item.error]));
  }, [disabledOptions]);

  // Формирование опций с учётом disabledOptions
  useEffect(() => {
    let listOptions = prepareSelectOptions(data, dataTitle, dataValue, sort);
    if (listOptions && disabledOptionsMap.size > 0) {
      listOptions = listOptions.map(opt => {
        const error = disabledOptionsMap.get(opt.value);
        if (error) {
          return { ...opt, disabled: true, disabledReason: error };
        }
        return opt;
      });
    }
    if (listOptions) setOptions(listOptions);
  }, [data, dataTitle, dataValue, sort, disabledOptionsMap]);

  useEffect(() => {
    const selectedValue = options?.find(item => String(item.value) === String(value));
    setCurrentValue(selectedValue || null);
  }, [value, options]);

  // Автоопределение цветов
  const hasAnyColor = useMemo(() => {
    if (forceShowColorIndicator === true) return true;
    if (forceShowColorIndicator === false) return false;
    return Array.isArray(data) && data.some((item: any) => item?.color && typeof item.color === 'string' && item.color.trim() !== '');
  }, [data, forceShowColorIndicator]);

  // Кастомный рендер опций и выбранного значения
  const formatOptionLabel = useMemo(() => {
    if (!hasAnyColor) {
      // Обычный текст (без цветов)
      return ({ label, disabled: optDisabled, disabledReason }, { context }) => {
        if (context === 'menu' && optDisabled && disabledReason) {
          return (
            <div style={ { opacity: 0.6, color: '#6c757d' } }>
              <div>{ label }</div>
              <div className='text-danger' style={ { fontSize: '12px', marginTop: '2px' } }>{ disabledReason }</div>
            </div>
          );
        }
        return <span>{ label }</span>;
      };
    }

    // С цветовыми индикаторами
    return ({ label, color, disabled: optDisabled, disabledReason }, { context }) => {
      // Рендер в выпадающем списке
      if (context === 'menu') {
        const disabledStyle = optDisabled ? { opacity: 0.6, color: '#6c757d' } : {};
        return (
          <div style={ { ...disabledStyle } }>
            <div style={ { display: 'flex', alignItems: 'center' } }>
              <ColorSwatch color={ color } shape={ indicatorShape } />
              <span>{ label }</span>
            </div>
            { optDisabled && disabledReason && (
              <div className='text-danger' style={ { fontSize: '12px', marginTop: '2px', marginLeft: '22px' } }>
                { disabledReason }
              </div>
            ) }
          </div>
        );
      }
      // Рендер выбранного значения
      if (context === 'value' && colorInSelectedValue) {
        return (
          <div style={ { display: 'flex', alignItems: 'center' } }>
            <ColorSwatch color={ color } shape={ indicatorShape } />
            <span>{ label }</span>
          </div>
        );
      }
      return <span>{ label }</span>;
    };
  }, [hasAnyColor, colorInSelectedValue, indicatorShape]);

  // Комбинированная функция isOptionDisabled (мёрджит disabledOptions + внешний restIsOptionDisabled)
  const finalIsOptionDisabled = useCallback((opt) => {
    if (opt.disabled) return true;
    if (typeof restIsOptionDisabled === 'function') {
      return restIsOptionDisabled(opt, undefined);
    }
    return false;
  }, [restIsOptionDisabled]);

  // Стабильный onChange: иначе каждый рендер порождал новую функцию и заставлял
  // react-select ре-рендериться + ломал его внутренние useMemo, завязанные на identity коллбэка.
  const handleChange = useCallback((selectedValue) => {
    setCurrentValue(selectedValue);
    if (typeof onChange === 'function') {
      onChange({
        target: {
          name,
          value: selectedValue?.value,
          rawValue: selectedValue,
        },
      });
    }
  }, [name, onChange]);

  // commonProps мемоизирован: ссылка стабильна, пока не изменились реальные входы.
  // ВАЖНО про порядок спреда: `...rest` вынесен из commonProps в JSX (туда же, ПЕРЕД commonProps).
  // Если бы rest остался внутри — сам объект rest (новая ссылка каждый рендер) инвалидировал бы
  // useMemo и мемоизация теряла смысл. Поведение идентично прежнему:
  //  • ключи rest (placeholder, menuPlacement, components, options, formatOptionLabel, styles, ...)
  //    НЕ пересекаются с ключами commonProps — isClearable/isDisabled/isLoading/className/
  //    classNamePrefix/noOptionsMessage вытащены из rest в отдельные переменные;
  //  • внутренние ключи (formatOptionLabel, styles, value, options, menuPortalTarget, ...) идут
  //    в commonProps вторыми и так же корректно перебивают rest, как и раньше.
  const commonProps = useMemo(() => ({
    ref: ref as any, // forwardRef<HTMLElement> → react-select ждёт ref на экземпляр Select; runtime идентичен, типы разойдутся
    form: name,
    name,
    value: currentValue,
    options,
    // Приоритет — за явным consumer-пропсом, иначе наш алиас/дефолт.
    isClearable: restIsClearable !== undefined ? restIsClearable : clearable,
    isDisabled: restIsDisabled !== undefined ? restIsDisabled : (disabled || loading),
    isLoading: restIsLoading !== undefined ? restIsLoading : loading,
    className: restClassName ? `${ restClassName } react-select-container` : 'react-select-container',
    classNamePrefix: restClassNamePrefix ?? 'react-select',
    noOptionsMessage: restNoOptionsMessage ?? defaultNoOptionsMessage,
    // Внутренняя логика — её rest перебить не может:
    isOptionDisabled: finalIsOptionDisabled, // мёрджит disabledOptions + restIsOptionDisabled
    onChange: handleChange,
    menuPortalTarget: document.body,
    styles: menuPortalStyles,
    isSearchable,
    formatOptionLabel,
  }), [
    ref, name, currentValue, options,
    restIsClearable, clearable,
    restIsDisabled, disabled, loading, restIsLoading,
    restClassName, restClassNamePrefix, restNoOptionsMessage,
    finalIsOptionDisabled, handleChange,
    isSearchable, formatOptionLabel,
  ]);

  // Стабильные коллбэки для creatable-варианта: иначе formatCreateLabel/onCreateOption
  // получали бы новую ссылку каждый рендер и мемоизация MemoizedReactSelectCreatable не сработала бы.
  const handleCreateOption = useCallback((v: string) => {
    onCreateOption?.(v);
  }, [onCreateOption]);

  const formatCreateLabel = useCallback((v: string) => (
    <Button variant='primary' icon='plus' text={ `Добавить ${ v }` } onClick={ () => {} } />
  ), []);

  return (
    <Form.Group controlId={ controlId } className={ `${ rootClassName ?? '' }${ rowDesign ? ' row-design' : '' } ${ rowDesignWithoutLabel ? 'row-design-without-label' : '' }${ error ? ' field-has-error' : '' }${ isNextField && !error && !disabled ? ' field-is-next' : '' }` }>
      { label && <Form.Label className={ required ? 'required' : null }>{ label }</Form.Label> }
      { isCreatable ? (
        <MemoizedReactSelectCreatable
          { ...rest }
          { ...commonProps }
          formatCreateLabel={ formatCreateLabel }
          onCreateOption={ handleCreateOption }
        />
      ) : (
        <MemoizedReactSelect { ...rest } { ...commonProps } />
      ) }
      { error && <small className='form-text text-danger'>{ error }</small> }
    </Form.Group>
  );
});

// ----------------------------------------------------------------------
// PropTypes с подробными комментариями. AI: НЕ УДАЛЯТЬ КОММЕНТАРИИ!
// ----------------------------------------------------------------------

SimpleSelectInput.displayName = 'SimpleSelectInput';

export default SimpleSelectInput;